import { FormEvent, useState } from 'react';
import { toast } from 'sonner';
import { Layout } from '@/components/layout/Layout';
import { PageHeader } from '@/components/ui/PageHeader';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { Spinner } from '@/components/ui/Spinner';
import { Dialog } from '@/components/ui/Dialog';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useSubmitFeedback, useUserFeedback } from '@/hooks/useFeedback';
import { extractValidationErrors, getErrorMessage } from '@/utils/errorHandler';
import {
    CheckCircleIcon,
    ChatBubbleLeftIcon,
    EyeIcon,
} from '@heroicons/react/24/outline';
import type { FeedbackReason, FeedbackStatus, UserFeedback } from '@/api/types';

const REASON_OPTIONS: { value: FeedbackReason; label: string }[] = [
    { value: 'general_inquiry', label: 'General Inquiry' },
    { value: 'technical_issue', label: 'Technical Issue' },
    { value: 'billing_question', label: 'Billing Question' },
    { value: 'other', label: 'Other' },
];

function getStatusColor(status: FeedbackStatus): string {
    switch (status) {
        case 'new':
            return 'bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400';
        case 'read':
            return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-400';
        case 'resolved':
            return 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400';
        default:
            return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
    }
}

function FeedbackForm({
    onSuccess,
}: {
    onSuccess: () => void;
}) {
    const [reason, setReason] = useState<FeedbackReason>('general_inquiry');
    const [message, setMessage] = useState('');
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [generalError, setGeneralError] = useState<string | null>(null);

    const submitFeedback = useSubmitFeedback();

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        setErrors({});
        setGeneralError(null);

        if (message.trim().length < 10) {
            setErrors({ message: 'Message must be at least 10 characters' });
            return;
        }

        try {
            await submitFeedback.mutateAsync({ reason, message: message.trim() });
            toast.success('Feedback submitted successfully');
            setReason('general_inquiry');
            setMessage('');
            onSuccess();
        } catch (error) {
            const validationErrors = extractValidationErrors(error);
            if (Object.keys(validationErrors).length > 0) {
                setErrors(validationErrors);
            } else {
                setGeneralError(getErrorMessage(error));
            }
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            {generalError && (
                <ErrorAlert
                    message={generalError}
                    onDismiss={() => setGeneralError(null)}
                />
            )}

            <div className="max-w-xs">
                <Select
                    label="Reason"
                    value={reason}
                    onChange={setReason}
                    options={REASON_OPTIONS}
                    error={errors.reason}
                />
            </div>

            <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Message
                </label>
                <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    rows={5}
                    placeholder="Please describe your inquiry, issue, or question..."
                    className="block w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-base text-gray-900 focus:outline-hidden focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                />
                {errors.message && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                        {errors.message}
                    </p>
                )}
            </div>

            <div className="flex justify-end">
                <Button
                    type="submit"
                    variant="primary"
                    isLoading={submitFeedback.isPending}
                >
                    Submit Feedback
                </Button>
            </div>
        </form>
    );
}

function SubmissionsList({
    submissions,
    onViewDetails,
}: {
    submissions: UserFeedback[];
    onViewDetails: (submission: UserFeedback) => void;
}) {
    if (submissions.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center py-8 text-gray-500 dark:text-gray-400">
                <ChatBubbleLeftIcon className="mb-2 h-8 w-8" />
                <p>No submissions yet</p>
            </div>
        );
    }

    return (
        <div className="divide-y divide-gray-200 overflow-hidden dark:divide-accent">
            {submissions.map((submission) => {
                const date = new Date(submission.createdAt);
                return (
                    <div
                        key={submission.id}
                        className="flex items-start justify-between gap-4 py-4 first:pt-0 last:pb-0"
                    >
                        <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                                <span className="font-mono text-xs text-gray-500 dark:text-gray-400">
                                    #{submission.id}
                                </span>
                                <span className="text-sm font-medium text-gray-900 dark:text-white">
                                    {submission.reasonLabel}
                                </span>
                                <span
                                    className={`rounded-sm px-2 py-0.5 text-xs font-medium ${getStatusColor(submission.status)}`}
                                >
                                    {submission.statusLabel}
                                </span>
                            </div>
                            <p className="mt-1 truncate text-sm text-gray-600 dark:text-gray-400">
                                {submission.message}
                            </p>
                            <p className="mt-1 text-xs text-gray-500 dark:text-gray-500">
                                {date.toLocaleDateString()} {date.toLocaleTimeString()}
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={() => onViewDetails(submission)}
                            className="shrink-0 rounded-sm p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-300"
                            title="View details"
                        >
                            <EyeIcon className="h-5 w-5" />
                        </button>
                    </div>
                );
            })}
        </div>
    );
}

export function FeedbackPage() {
    usePageTitle('Feedback');

    const [showSuccess, setShowSuccess] = useState(false);
    const [viewTarget, setViewTarget] = useState<UserFeedback | null>(null);
    const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);

    const { data: submissions, isLoading } = useUserFeedback(10);

    const handleViewDetails = (submission: UserFeedback) => {
        setViewTarget(submission);
        setIsViewDialogOpen(true);
    };

    if (showSuccess) {
        return (
            <Layout>
                <div className="space-y-6">
                    <PageHeader title="Feedback" />
                    <div className="rounded-lg border border-gray-300 bg-white p-6 sm:p-8 text-center dark:border-gray-700 dark:bg-secondary">
                        <CheckCircleIcon className="mx-auto h-12 w-12 text-green-500" />
                        <h2 className="mt-4 text-lg font-semibold text-gray-900 dark:text-white">
                            Thank you for your feedback!
                        </h2>
                        <p className="mt-2 text-gray-600 dark:text-gray-300">
                            We&apos;ve received your message and will get back to
                            you if needed.
                        </p>
                        <Button
                            variant="secondary"
                            className="mt-6"
                            onClick={() => setShowSuccess(false)}
                        >
                            Back to Feedback
                        </Button>
                    </div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="space-y-6">
                <PageHeader title="Feedback" />

                <div className="grid gap-6 lg:grid-cols-2">
                    {/* Submit Form */}
                    <div className="rounded-lg border border-gray-300 bg-white p-4 sm:p-6 dark:border-gray-700 dark:bg-secondary">
                        <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
                            Contact Us
                        </h2>
                        <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">
                            Have a question, issue, or suggestion? Let us know and we&apos;ll
                            get back to you.
                        </p>
                        <FeedbackForm onSuccess={() => setShowSuccess(true)} />
                    </div>

                    {/* Previous Submissions */}
                    <div className="overflow-hidden rounded-lg border border-gray-300 bg-white p-4 sm:p-6 dark:border-gray-700 dark:bg-secondary">
                        <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
                            Your Submissions
                        </h2>
                        {isLoading ? (
                            <div className="flex justify-center py-8">
                                <Spinner size="md" />
                            </div>
                        ) : (
                            <SubmissionsList
                                submissions={submissions ?? []}
                                onViewDetails={handleViewDetails}
                            />
                        )}
                    </div>
                </div>
            </div>

            {/* Submission Detail Dialog */}
            <Dialog
                open={isViewDialogOpen}
                onClose={() => setIsViewDialogOpen(false)}
                afterLeave={() => setViewTarget(null)}
                title={viewTarget ? `Ticket #${viewTarget.id}` : 'Submission Details'}
                size="lg"
            >
                {viewTarget && (
                    <div className="space-y-4">
                        <div className="grid gap-3 text-sm">
                            <div className="flex flex-wrap gap-x-2 gap-y-1">
                                <span className="font-medium text-gray-500 dark:text-gray-400">
                                    Reason:
                                </span>
                                <span className="text-gray-900 dark:text-white">
                                    {viewTarget.reasonLabel}
                                </span>
                            </div>
                            <div className="flex flex-wrap gap-x-2 gap-y-1">
                                <span className="font-medium text-gray-500 dark:text-gray-400">
                                    Date:
                                </span>
                                <span className="text-gray-900 dark:text-white">
                                    {new Date(viewTarget.createdAt).toLocaleString()}
                                </span>
                            </div>
                            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                                <span className="font-medium text-gray-500 dark:text-gray-400">
                                    Status:
                                </span>
                                <span
                                    className={`rounded-sm px-2 py-0.5 text-xs font-medium ${getStatusColor(viewTarget.status)}`}
                                >
                                    {viewTarget.statusLabel}
                                </span>
                            </div>
                        </div>
                        <div>
                            <p className="mb-2 font-medium text-gray-500 dark:text-gray-400">
                                Message:
                            </p>
                            <div className="whitespace-pre-wrap rounded-lg bg-gray-50 p-4 text-gray-900 dark:bg-gray-900 dark:text-white">
                                {viewTarget.message}
                            </div>
                        </div>
                    </div>
                )}
            </Dialog>
        </Layout>
    );
}
